import { createFileRoute } from "@tanstack/react-router";
import { addDays } from "date-fns";
import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { z } from "zod";
import { useShallow } from "zustand/react/shallow";
import { trpc } from "@/app/api/trpcClient";
import { GanttSection } from "@/app/components/workstreams/GanttSection";
import { WorkstreamDrawer } from "@/app/components/workstreams/WorkstreamDrawer";
import type {
  GanttData,
  Person,
  Team,
  TimelineMode,
  Workstream,
  WorkstreamDraft,
  WorkstreamFilters,
} from "@/app/components/workstreams/types";
import { useDagStore } from "@/app/hooks/useDagStore";
import { useDataSync } from "@/app/hooks/useDataSync";
import { toCalendarDateInput } from "@/app/utils/workstreams/dateUtils";

const zSearch = z.object({
  personId: z.string().optional(),
});

export const Route = createFileRoute("/workstreams")({
  component: WorkstreamsPage,
  validateSearch: zSearch,
});

const teamColors = [
  "#2563eb",
  "#0f766e",
  "#7c3aed",
  "#be123c",
  "#b45309",
  "#4d7c0f",
  "#0369a1",
  "#a21caf",
  "#c2410c",
  "#4338ca",
];

function WorkstreamsPage() {
  const { isLoading } = useDataSync();
  const utils = trpc.useUtils();
  const teamMembers = useDagStore(useShallow((state) => state.teamMembersFiltered));
  const allTeams = useDagStore(useShallow((state) => state.allTeams));
  const search = Route.useSearch();
  const [timelineMode, setTimelineMode] = useState<TimelineMode>("month");
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedQuarter, setSelectedQuarter] = useState(Math.floor(new Date().getMonth() / 3));
  const [filters, setFilters] = useState<WorkstreamFilters>({
    teamId: "all",
    personId: search.personId ?? "all",
  });
  const [draft, setDraft] = useState<WorkstreamDraft | null>(null);
  const [previewDates, setPreviewDates] = useState<
    Map<string, Pick<Workstream, "startDate" | "endDate">>
  >(new Map());

  useEffect(() => {
    const personId = search.personId;
    if (!personId) return;
    setFilters((current) =>
      current.personId === personId ? current : { ...current, personId },
    );
  }, [search.personId]);

  const workstreamsQuery = trpc.workstreams.list.useQuery(undefined, {
    staleTime: 30 * 1000,
  });

  const createMutation = trpc.workstreams.create.useMutation({
    onSuccess: async () => {
      await utils.workstreams.invalidate();
      toast.success("Workstream created");
      setDraft(null);
    },
    onError: (error) => toast.error(error.message),
  });

  const updateMutation = trpc.workstreams.update.useMutation({
    onSuccess: async () => {
      await utils.workstreams.invalidate();
      toast.success("Workstream updated");
      setDraft(null);
      setPreviewDates(new Map());
    },
    onError: async (error) => {
      toast.error(error.message);
      setPreviewDates(new Map());
      await utils.workstreams.invalidate();
    },
  });

  const deleteMutation = trpc.workstreams.delete.useMutation({
    onSuccess: async () => {
      await utils.workstreams.invalidate();
      toast.success("Workstream deleted");
      setDraft(null);
    },
    onError: (error) => toast.error(error.message),
  });

  const data = useMemo(() => {
    const teams = mapTeams(allTeams);
    const people = teamMembers.map<Person>((member) => ({
      id: member.id,
      name: member.name,
      nickname: member.name,
      primaryTeamId: member.team || "Unassigned",
    }));

    const workstreams = (workstreamsQuery.data ?? []).map<Workstream>((workstream) => {
      const preview = previewDates.get(workstream.id);
      return {
        id: workstream.id,
        title: workstream.title,
        personId: workstream.personId,
        teamId: workstream.teamId ?? "Unassigned",
        startDate: preview?.startDate ?? workstream.startDate,
        endDate: preview?.endDate ?? workstream.endDate,
        originalDurationDays: workstream.originalDurationDays,
        referenceUrl: workstream.referenceUrl ?? undefined,
        createdAt: workstream.createdAt,
        updatedAt: workstream.updatedAt,
      };
    });

    return { teams, people, workstreams } satisfies GanttData;
  }, [allTeams, previewDates, teamMembers, workstreamsQuery.data]);

  function openNewWorkstream(personId?: string, startDate?: string, endDate?: string) {
    const person = personId ? data.people.find((item) => item.id === personId) : data.people[0];
    if (!person) {
      toast.error("No TeamDash members are loaded yet");
      return;
    }

    const start = startDate ?? toCalendarDateInput(new Date());
    const end = endDate ?? toCalendarDateInput(addDays(new Date(`${start}T00:00:00`), 7));
    setDraft({
      title: "",
      personId: person.id,
      teamId: person.primaryTeamId,
      startDate: start,
      endDate: end,
      referenceUrl: "",
    });
  }

  function openEditWorkstream(workstream: Workstream) {
    setDraft({
      id: workstream.id,
      title: workstream.title,
      personId: workstream.personId,
      teamId: workstream.teamId,
      startDate: workstream.startDate,
      endDate: workstream.endDate,
      referenceUrl: workstream.referenceUrl ?? "",
    });
  }

  function submitDraft() {
    if (!draft) return;
    const payload = {
      title: draft.title.trim(),
      personId: draft.personId,
      teamId: draft.teamId || null,
      startDate: draft.startDate,
      endDate: draft.endDate,
      referenceUrl: draft.referenceUrl.trim() || null,
    };

    if (draft.id) {
      updateMutation.mutate({ id: draft.id, updates: payload });
      return;
    }

    createMutation.mutate(payload);
  }

  function moveWorkstream(workstream: Workstream, startDate: string, endDate: string) {
    updateMutation.mutate({
      id: workstream.id,
      updates: {
        startDate: toCalendarDateInput(startDate),
        endDate: toCalendarDateInput(endDate),
      },
    });
  }

  function previewWorkstreamMove(id: string, startDate: string, endDate: string) {
    setPreviewDates((current) => {
      const next = new Map(current);
      next.set(id, {
        startDate: toCalendarDateInput(startDate),
        endDate: toCalendarDateInput(endDate),
      });
      return next;
    });
  }

  function cancelPreviewWorkstreamMove(id: string) {
    setPreviewDates((current) => {
      const next = new Map(current);
      next.delete(id);
      return next;
    });
  }

  const isSaving =
    createMutation.isPending || updateMutation.isPending || deleteMutation.isPending;
  const isInitialLoading = isLoading || workstreamsQuery.isLoading;

  return (
    <div className="h-[calc(100vh-84px)] w-full min-w-0 max-w-full overflow-hidden bg-stone-50 px-6 pb-6 md:max-w-[calc(100vw-var(--sidebar-width))]">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-stone-950">Workstreams</h1>
          <p className="text-sm text-stone-600">Plan current team work across a shared timeline.</p>
        </div>
        <button
          type="button"
          className="rounded-md bg-stone-950 px-3 py-2 text-sm font-medium text-white shadow-xs hover:bg-stone-800 disabled:cursor-not-allowed disabled:opacity-50"
          disabled={data.people.length === 0}
          onClick={() => openNewWorkstream()}
        >
          New workstream
        </button>
      </div>

      {isInitialLoading && data.people.length === 0 ? (
        <div className="grid h-[calc(100vh-180px)] place-items-center rounded-lg border bg-white text-sm text-stone-600">
          Loading workstreams...
        </div>
      ) : (
        <GanttSection
          data={data}
          filters={filters}
          selectedMonth={selectedMonth}
          selectedQuarter={selectedQuarter}
          timelineMode={timelineMode}
          onChangeFilters={setFilters}
          onChangeMonth={setSelectedMonth}
          onChangeQuarter={setSelectedQuarter}
          onChangeTimelineMode={setTimelineMode}
          onCreateWorkstream={openNewWorkstream}
          onDeleteWorkstream={(id) => deleteMutation.mutate({ id })}
          onEditWorkstream={openEditWorkstream}
          onMoveWorkstream={moveWorkstream}
          onCancelPreviewMove={cancelPreviewWorkstreamMove}
          onPreviewMove={previewWorkstreamMove}
        />
      )}

      {draft && (
        <WorkstreamDrawer
          data={data}
          draft={draft}
          isSaving={isSaving}
          onChangeDraft={setDraft}
          onClose={() => setDraft(null)}
          onDelete={(id) => deleteMutation.mutate({ id })}
          onSubmit={submitDraft}
        />
      )}
    </div>
  );
}

function mapTeams(allTeams: string[]): Team[] {
  const teamNames = allTeams.length > 0 ? allTeams : ["Unassigned"];
  const teams = teamNames.map<Team>((team, index) => ({
    id: team,
    name: team,
    color: teamColors[index % teamColors.length] ?? "#64748b",
  }));

  if (!teams.some((team) => team.id === "Unassigned")) {
    teams.push({ id: "Unassigned", name: "Unassigned", color: "#64748b" });
  }

  return teams;
}
